/* =============================================================================
   Rynoka: toyland-sale.js — Sale page dynamics (loaded only on template page.sale)
   - Countdown lifecycle (upcoming / active / ended) from the hero's sale window
   - Believable "people shopping now" simulation (time-of-day seeded, gentle drift)
   - Deal of the Day "% claimed" derived from elapsed sale window
   - Seeded, per-product/day "X sold today" on cards
   - In-page category pill filtering
   No dependencies; safe to run when any piece is absent.
   ============================================================================= */
(function () {
  'use strict';

  // ── tiny seeded RNG helpers (deterministic → stable across reloads) ─────────
  function hashStr(s) {
    var h = 2166136261 >>> 0;
    for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return h >>> 0;
  }
  function mulberry32(a) {
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
  function parseTS(str) {
    if (!str) return null;
    var t = new Date(str).getTime();
    return isNaN(t) ? null : t;
  }

  // Evening-peaked daily weights (index = hour 0–23, peak ~20:00)
  var HOUR_W = [
    0.15, 0.10, 0.08, 0.06, 0.06, 0.08,
    0.12, 0.20, 0.30, 0.42, 0.52, 0.60,
    0.66, 0.70, 0.68, 0.66, 0.70, 0.78,
    0.86, 0.94, 1.00, 0.92, 0.66, 0.36
  ];

  function init() {
    var hero = document.querySelector('[data-sale-hero]');
    var deal = document.querySelector('[data-sale-deal]');

    // ── Sale window (from hero; shared by countdown + deal claimed) ──────────
    var startTS = hero ? parseTS(hero.getAttribute('data-sale-start')) : null;
    var endTS = hero ? parseTS(hero.getAttribute('data-sale-end')) : null;
    var labelEnds = (hero && hero.getAttribute('data-label-ends')) || 'Ends in';
    var labelStarts = (hero && hero.getAttribute('data-label-starts')) || 'Starts in';

    // Countdown DOM
    var badgeText = hero && hero.querySelector('[data-sale-badge-text]');
    var cdWrap = hero && hero.querySelector('[data-sale-countdown]');
    var cdDaysBox = hero && hero.querySelector('[data-cd-days]');
    var elDays = hero && hero.querySelector('[data-cd-num-days]');
    var elHours = hero && hero.querySelector('[data-cd-num-hours]');
    var elMins = hero && hero.querySelector('[data-cd-num-minutes]');
    var elSecs = hero && hero.querySelector('[data-cd-num-seconds]');

    // Live shoppers DOM
    var liveEl = hero && hero.querySelector('[data-sale-live]');
    var liveCountEl = hero && hero.querySelector('[data-sale-live-count]');
    var liveMin = liveEl ? parseInt(liveEl.getAttribute('data-min'), 10) || 400 : 400;
    var liveMax = liveEl ? parseInt(liveEl.getAttribute('data-max'), 10) || 900 : 900;
    if (liveMax < liveMin) { var tmp = liveMax; liveMax = liveMin; liveMin = tmp; }
    var liveCurrent = null;
    var liveMaxDev = Math.max(4, Math.round((liveMax - liveMin) * 0.03));

    // Deal claimed DOM
    var dealBar = deal && deal.querySelector('[data-sale-claimed-bar]');
    var dealText = deal && deal.querySelector('[data-sale-claimed-text]');
    var claimedFloor = deal ? parseInt(deal.getAttribute('data-claimed-floor'), 10) || 10 : 10;
    var claimedTarget = deal ? parseInt(deal.getAttribute('data-claimed-target'), 10) || 92 : 92;

    var ended = false;

    function liveBase(now) {
      var d = new Date(now);
      var hour = d.getHours();
      var w = HOUR_W[hour];
      var dayKey = d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
      var rnd = mulberry32(hashStr(dayKey + '|' + hour));
      var noise = (rnd() - 0.5) * (liveMax - liveMin) * 0.06;
      return clamp(Math.round(liveMin + (liveMax - liveMin) * w + noise), liveMin, liveMax);
    }

    function updateLive(now) {
      if (!liveCountEl) return;
      var base = liveBase(now);
      if (liveCurrent === null) liveCurrent = base;
      var delta = [-3, -2, -1, 1, 2, 3][Math.floor(Math.random() * 6)];
      liveCurrent += delta;
      if (liveCurrent < base - liveMaxDev) liveCurrent = base - liveMaxDev + 1;
      if (liveCurrent > base + liveMaxDev) liveCurrent = base + liveMaxDev - 1;
      liveCurrent = clamp(Math.round(liveCurrent), liveMin, liveMax);
      liveCountEl.textContent = liveCurrent.toLocaleString('en-IN');
    }

    function updateClaimed(now) {
      if (!deal) return;
      var frac;
      if (startTS !== null && endTS !== null && endTS > startTS) {
        frac = clamp((now - startTS) / (endTS - startTS), 0, 1);
      } else {
        frac = 0; // unknown window → sit at floor
      }
      var pct = Math.round(claimedFloor + (claimedTarget - claimedFloor) * frac);
      pct = clamp(pct, 0, 99);
      if (dealBar) dealBar.style.width = pct + '%';
      if (dealText) dealText.textContent = pct + '% claimed';
    }

    function renderCountdown(target, label, now) {
      var rem = Math.max(0, target - now);
      var totalSec = Math.floor(rem / 1000);
      var days = Math.floor(totalSec / 86400);
      var hours = Math.floor((totalSec % 86400) / 3600);
      var mins = Math.floor((totalSec % 3600) / 60);
      var secs = totalSec % 60;

      if (cdDaysBox) {
        if (days > 0) { cdDaysBox.hidden = false; if (elDays) elDays.textContent = pad2(days); }
        else { cdDaysBox.hidden = true; }
      }
      if (elHours) elHours.textContent = pad2(hours);
      if (elMins) elMins.textContent = pad2(mins);
      if (elSecs) elSecs.textContent = pad2(secs);

      if (badgeText) {
        badgeText.textContent = days > 0
          ? (label + ' ' + days + 'd ' + hours + 'h')
          : (label + ' ' + pad2(hours) + 'h ' + pad2(mins) + 'm');
      }
    }

    function setEnded() {
      ended = true;
      if (hero) hero.classList.add('is-ended');
    }

    var tick = 0;
    function loop() {
      var now = Date.now();

      // Phase resolution
      if (startTS !== null && now < startTS) {
        if (hero) hero.classList.remove('is-ended');
        renderCountdown(startTS, labelStarts, now);
      } else if (endTS !== null && now < endTS) {
        if (hero) hero.classList.remove('is-ended');
        renderCountdown(endTS, labelEnds, now);
      } else if (endTS !== null && now >= endTS) {
        if (!ended) setEnded();
      } else {
        // No end configured → hide the countdown boxes, keep badge label only
        if (cdWrap) cdWrap.style.display = 'none';
        if (badgeText) badgeText.textContent = labelEnds;
      }

      if (!ended) {
        if (tick % 4 === 0) updateLive(now);   // gentle live drift every ~4s
        updateClaimed(now);
      }
      tick++;
    }

    // Initial paint (no flash), then tick each second
    loop();
    var timer = setInterval(function () {
      loop();
      if (ended) clearInterval(timer);
    }, 1000);

    // ── "X sold today" — seeded per product per day (stable) ────────────────
    var dnow = new Date();
    var soldDayKey = dnow.getFullYear() + '-' + (dnow.getMonth() + 1) + '-' + dnow.getDate();
    document.querySelectorAll('[data-sale-sold]').forEach(function (el) {
      var seed = el.getAttribute('data-seed') || '0';
      var rnd = mulberry32(hashStr(seed + '|' + soldDayKey));
      var n = 6 + Math.floor(rnd() * 40); // 6–45
      el.textContent = n + ' sold today';
    });

    // ── Category pill filtering ─────────────────────────────────────────────
    var pillsWrap = document.querySelector('[data-sale-pills]');
    if (pillsWrap) {
      var cats = Array.prototype.slice.call(document.querySelectorAll('[data-sale-cat]'));
      pillsWrap.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-sale-pill]');
        if (!btn) return;
        var val = btn.getAttribute('data-sale-pill');
        pillsWrap.querySelectorAll('[data-sale-pill]').forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        // Exactly one department is shown at a time (there is no "All" pill).
        cats.forEach(function (sec) {
          sec.hidden = sec.getAttribute('data-sale-cat') !== val;
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
